/**
 * Règles du groupe Planifier ⑤ (vague 4 du lot U6b) — pures, testées sans écran.
 *
 * LIMITE DE TRAVAIL EN COURS (ARB-r2-05, AGILE-08, SRF-PRJ-031)
 * -------------------------------------------------------------
 * La limite se règle PAR COLONNE du tableau du projet, dans Planifier › Sprints. Elle se compte
 * sur le SPRINT ACTIF ; sans sprint actif, sur TOUT le tableau. « Atteinte » = ambre (prudence),
 * « dépassée » = rouge : un avertissement, jamais un blocage, et jamais la coche verte — une
 * colonne sous sa limite n'est pas une réussite, c'est l'état normal.
 *
 * La passerelle ne porte pas encore de limite (le modèle `Sprint` n'a ni capacité ni limite ;
 * les colonnes du tableau sont celles du gabarit). Les écrans affichent donc le COMPTE selon la
 * bonne portée et la mention « à venir » ; dès qu'une limite existe, `etatLimite` la juge.
 *
 * PRÊT POUR LA COLONIE (§ 4.2 Tranche, AGILE-01/02, SRF-PRJ-003/004)
 * ------------------------------------------------------------------
 * Une tranche est un ÉTAT calculé d'une Feature, jamais une case à cocher. La passerelle ne
 * calcule pas encore la préparation (reçu Spec Gate frais par US, tests conçus, taille
 * estimée) : aucune feature n'est donc déclarée prête par l'écran. On ne fait que nommer les
 * manques VISIBLES dans les données (US sans critère, feature sans US, sans source).
 */
import type { BacklogItem, Sprint } from '@/api/client'

// ---------------------------------------------------------------------------------------------
// Limite de travail en cours
// ---------------------------------------------------------------------------------------------

export type EtatLimite = 'sans-limite' | 'sous-la-limite' | 'atteinte' | 'depassee'

/** Limites par statut de colonne ; absent ou nul = colonne sans limite. */
export type LimitesEnCours = Partial<Record<string, number | null>>

/** Juge un compte contre une limite. Une limite nulle, négative ou non entière est ignorée. */
export function etatLimite(compte: number, limite: number | null | undefined): EtatLimite {
  if (limite == null || !Number.isInteger(limite) || limite <= 0) return 'sans-limite'
  if (compte > limite) return 'depassee'
  if (compte === limite) return 'atteinte'
  return 'sous-la-limite'
}

export interface PorteeLimite {
  /** `sprint` : comptée sur le sprint actif ; `tableau` : sans sprint actif, tout le tableau. */
  portee: 'sprint' | 'tableau'
  sprint: Sprint | null
}

/**
 * Le sprint actif qui porte le compte. Plusieurs sprints actifs (la passerelle ne l'interdit
 * pas) : le plus ancien par date de début, puis par identifiant — un choix stable, jamais
 * l'ordre de réponse de l'API.
 */
export function porteeLimite(sprints: readonly Sprint[]): PorteeLimite {
  const actifs = sprints.filter((s) => s.status === 'active')
  if (actifs.length === 0) return { portee: 'tableau', sprint: null }
  const tries = [...actifs].sort((a, b) => {
    const da = a.starts_on ?? '9999-12-31'
    const db = b.starts_on ?? '9999-12-31'
    return da === db ? a.id - b.id : da.localeCompare(db)
  })
  return { portee: 'sprint', sprint: tries[0]! }
}

/** Compte les cartes de chaque colonne selon la portée d'ARB-r2-05. */
export function compterColonnes(
  cartes: readonly Pick<BacklogItem, 'status' | 'sprint_id'>[],
  statuts: readonly string[],
  sprints: readonly Sprint[],
): PorteeLimite & { comptes: Record<string, number> } {
  const p = porteeLimite(sprints)
  const comptes: Record<string, number> = Object.fromEntries(statuts.map((s) => [s, 0]))
  for (const c of cartes) {
    if (!(c.status in comptes)) continue
    if (p.sprint && c.sprint_id !== p.sprint.id) continue
    comptes[c.status] = (comptes[c.status] ?? 0) + 1
  }
  return { ...p, comptes }
}

// ---------------------------------------------------------------------------------------------
// Prêt pour la colonie
// ---------------------------------------------------------------------------------------------

export interface LigneTranche {
  feature: BacklogItem
  stories: BacklogItem[]
  /** Manques nommés (AC-P8) : ceux de la passerelle, sinon ceux visibles dans les données. */
  manques: string[]
  /** `prete` / `non-prete` : calcul serveur (DoR) ; `non-evaluee` : repli client, jamais « prête ». */
  etat: 'faite' | 'prete' | 'non-prete' | 'non-evaluee'
  /** Critères que la passerelle ne sait pas encore évaluer (taille de contexte, lignes de revue). */
  nonEvalues?: string[]
}

/** DoR d'un item, telle que `GET /workspaces/{id}/backlog/readiness` la calcule. */
export interface PretServeur { id: number; prete: boolean; manques: readonly string[]; non_evalues: readonly string[] }

/** Libellé de chaque manque de la définition de prêt (codes de `PlanificationService`). */
export const LIBELLES_MANQUE_DOR: Record<string, string> = {
  NON_ACCEPTEE: 'US non acceptée', CRITERES_ABSENTS: 'critères d’acceptation absents', LIEN_REQ_ABSENT: 'aucun lien vers une exigence',
  RECU_SPEC_GATE_ABSENT: 'reçu Spec Gate absent', ESTIMATION_ABSENTE: 'estimation absente', TESTS_NON_CONCUS: 'tests non conçus',
  DEPENDANCE_OUVERTE: 'dépendance bloquante ouverte', OBJECTIF_ABSENT: 'objectif absent', PERIMETRE_NON_VALIDE: 'périmètre non validé',
  RISQUES_NON_IDENTIFIES: 'risques non identifiés', TRACABILITE_ABSENTE: 'traçabilité absente', US_ABSENTES: 'aucune US',
  US_NON_PRETES: 'US non prêtes', TAILLE_CONTEXTE_32K: 'taille ≤ 32 K de contexte', LIGNES_REVUE_400: '≤ 400 lignes de revue',
}

export function libelleManqueDor(code: string): string {
  return LIBELLES_MANQUE_DOR[code] ?? code
}

type Traduire = (cle: string, repli: string, vars?: Record<string, string | number>) => string

/**
 * Une ligne par Feature. `faite` si la feature est terminée ; sinon `non-evaluee` : sans le
 * calcul serveur, l'écran ne marque jamais une feature « prête » (planche, état « erreur » :
 * « aucune feature n'est marquée prête par défaut »).
 */
export function lignesTranches(items: readonly BacklogItem[], t: Traduire, dor?: ReadonlyMap<number, PretServeur>): LigneTranche[] {
  return items
    .filter((i) => i.kind === 'feature')
    .map((feature) => {
      const stories = items.filter((i) => i.parent_id === feature.id && i.kind === 'story')
      const serveur = dor?.get(feature.id)
      if (serveur) {
        return {
          feature, stories,
          manques: serveur.manques.map((m) => t(`planifier.dor.${m}`, libelleManqueDor(m))),
          etat: feature.status === 'done' ? 'faite' : serveur.prete ? 'prete' : 'non-prete',
          nonEvalues: serveur.non_evalues.map((m) => t(`planifier.dor.${m}`, libelleManqueDor(m))),
        }
      }
      const manques: string[] = []
      if (stories.length === 0) manques.push(t('planifier.manqueSansUS', 'aucune US'))
      for (const s of stories) {
        if (s.acceptance_criteria.length === 0) {
          manques.push(t('planifier.manqueCriteres', '#{{id}} sans critère d’acceptation', { id: s.id }))
        }
      }
      if (!feature.source_spec && stories.every((s) => !s.source_spec)) {
        manques.push(t('planifier.manqueSource', 'aucune spécification source'))
      }
      return { feature, stories, manques, etat: feature.status === 'done' ? 'faite' : 'non-evaluee' }
    })
}

/** Statuts de mission qui occupent la colonie (ni brouillon, ni terminés). */
const MISSION_ACTIVE = new Set(['plan_proposed', 'plan_approved', 'running', 'suspended'])

export function compterMissionsActives(missions: readonly { status: string }[]): number {
  return missions.filter((m) => MISSION_ACTIVE.has(m.status)).length
}
